import { useContext, useEffect, useState } from "react";
import * as turf from "@turf/turf";

// context
import MapContext from "../../../services/context/map-context";
import { useAppContext } from "../../../services/context/app-context";

// utils
import {
  addClipLayer,
  addLayerIfNotExists,
  addOrUpdateSource,
  addPropertyAreaLabel,
  addPropertyDistanceLabels,
  addPropertyVertexLabels,
  addPropertyVertexPointLayer,
  isMapExist,
  removeAllPropertyLabels,
  removeClipLayer,
  removeLayerAndSource,
  removePropertyVertexPointLayer,
} from "../../../utils/map-layer";
import { createDistanceAndVertexFeatures } from "../../../utils/map-labels";
import { polygonToLines } from "../../../utils/map-polygon";
import { formatArea } from "../../../utils/formatting";



export const PropertyViewOnly = ({
  propertyGeoJSON,
}) => {
  const { map, mapLoaded } = useContext(MapContext);
  const { showBaseBuildings } = useAppContext();
  const [edgesLayerAdded, setEdgesLayerAdded] = useState(false);
  

  useEffect(() => {
    if (map && mapLoaded && propertyGeoJSON && !edgesLayerAdded) {
      if(!showBaseBuildings){
        addClipLayer(map, propertyGeoJSON);
      }
      
      addOrUpdateSource(map, "property-polygon", propertyGeoJSON);
      addLayerIfNotExists(map, {
        id: "property-polygon",
        type: "fill",
        source: "property-polygon",
        paint: {
          "fill-color": "#5E6278",
          "fill-opacity": 0.6,
          /* "fill-outline-color": "#343745", */
        },
      });

      setEdgesLayerAdded(true);
      addEdgesLayer(propertyGeoJSON);
      displayVertexAndDistanceLabels(propertyGeoJSON.features[0]);
    }

    // Cleanup on unmount
    return () => {
      if (map &&  isMapExist(map) && edgesLayerAdded) {
        
        removeLayerAndSource(map, "property-polygon", "property-polygon");
        removeLayerAndSource(map, "edges-layer", "edges");
        removeLayerAndSource(
          map,
          "property-entry-side-layer",
          "property-entry-side-source"
        );
        removePropertyVertexPointLayer(map);
        removeAllPropertyLabels(map);
        removeClipLayer(map);
        setEdgesLayerAdded(false);
      }
    };
  }, [map, mapLoaded, propertyGeoJSON, edgesLayerAdded]);

    // hide base buildings
  useEffect(() => {
    if (!showBaseBuildings && propertyGeoJSON) {
      addClipLayer(map, propertyGeoJSON);
    }
    if (showBaseBuildings) {
      removeClipLayer(map);
    }
  }, [showBaseBuildings]);

  const addEdgesLayer = (propertyGeoJSON) => {
    const lineFeatures = polygonToLines(propertyGeoJSON.features[0]);
    const edgesGeoJSON = {
      type: "FeatureCollection",
      features: lineFeatures,
    };

    addOrUpdateSource(map, "edges", edgesGeoJSON);
    addLayerIfNotExists(map, {
      id: "edges-layer",
      type: "line",
      source: "edges",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": "#5E6278", // not selected
        "line-width": 4,
        "line-opacity": 1,
        "line-dasharray": [1, 2],
      },
    });

  };

  // Show distance + vertex labels
  const displayVertexAndDistanceLabels = (polygon) => {
    const { distanceFeatures, vertexFeatures } =
      createDistanceAndVertexFeatures(polygon);
    const area = turf.area(polygon);
    const centroid = turf.center(polygon);

    addPropertyDistanceLabels(map, {
      type: "FeatureCollection",
      features: distanceFeatures,
    });

    addPropertyVertexLabels(map, {
      type: "FeatureCollection",
      features: vertexFeatures,
    });

    const explodedFC = turf.explode(polygon);
    addPropertyVertexPointLayer(map, explodedFC);

    addPropertyAreaLabel(map, {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: centroid.geometry,
          properties: {
            area: `${formatArea(area).value} ${formatArea(area).unit}`,
          },
        },
      ],
    });
  };

  return null;
};
